<template>
  <ClientOnly>
    <nav class="mediaAlbumList__Breadcrumbs">
      <ul>
        <li v-for="(item, index) in data.breadcrumbs" class="mediaAlbumList__BreadcrumbItem">
          <button v-if="index < data.breadcrumbs.length - 1" @click="fetchCategoryData(item)" :aria-label="$t('folder', { name: item.name })">
            <span v-text="index === 0 ? $t(`${category}Category`) : item.name" />
          </button>
          <span v-else>{{ index === 0 ? $t(`${category}Category`) : item.name }}</span>
        </li>
      </ul>
    </nav>

    <section v-if="results?.items" class="mediaAlbumList">
      <ul>
        <li v-for="(item, index) in results.items">
          <template v-if="item.type === 'folder'">
            <div class="mediaAlbumList__Folder">
              <Icon name="folder" />
              <button @click="fetchCategoryData(item)" class="mediaAlbumList__ItemButton" :aria-label="$t('folder', { name: item.name })">
                <span class="mediaAlbumList__ItemName" v-text="item.name" />
              </button>
            </div>

            <div class="mediaAlbumList__FolderActions">
              <div class="mediaAlbumList__ItemStatus">
                <span v-if="item.collections_count">{{ $t('folders', item.collections_count) }}</span>
                <template v-if="item.collections_count && item.media_count">, </template>
                <span v-if="item.media_count">{{ $t('photos', item.media_count) }}</span>
              </div>
              <Button medium secondary @click="fetchCategoryData(item)">{{ $t('show') }}</Button>
            </div>
          </template>
          <template v-else>
            <button @click="showModal(item)" :aria-label="$t('imageModal')" class="mediaAlbumList__ImageModalButton">
              <Flare v-if="IMAGES_FORMAT.includes(item.meta?.format)" :srcset="item" width="300" :alt="item.alt" class="mediaAlbumList__PreviewImage" />
              <span class="mediaAlbumList__ItemFilename" v-text="item.meta.original_name || item.title" />
            </button>

            <div class="mediaAlbumList__ItemActions">
              <span v-if="item.meta?.size">{{ convertToMB(item.meta.size) }}</span>
              <Button medium secondary @click="downloadMedia(item, index)" class="mediaAlbumList__DownloadButton" :class="{'isDownloading': downloadingIndex === index}" :disabled="downloadingIndex === index">
                <span class="mediaAlbumList__DownloadButtonText">
                  <template v-if="downloadingIndex === index">{{ $t('downloading') }}</template>
                  <template v-else>{{ $t('download') }}</template>
                </span>
                <img src="/resources/images/icons/download.svg" width="16" height="16" :alt="$t('download')" />
              </Button>
            </div>
          </template>
        </li>
      </ul>
    </section>

    <Modal v-if="dialogs.image" v-model="dialogs.image" class="mediaAlbumList__Modal">
      <FlarePicture v-if="IMAGES_FORMAT.includes(modalImage.meta?.format)" :src="{1920: { src: modalImage, width: 800 }}" class="mediaAlbumList__ModalImage" :alt="modalImage?.alt" />

      <div class="mediaAlbumList__ModalBottom">
        <span v-text="modalImage?.meta?.original_name || modalImage?.title || ''" />

        <div class="mediaAlbumList__ModalActions">
          <span v-if="modalImage?.meta?.size">{{ convertToMB(modalImage.meta.size) }}</span>
          <button @click="downloadMedia(modalImage)" class="mediaAlbumList__ModalDownloadButton">
            <img src="/resources/images/icons/download.svg" width="16" height="16" :alt="$t('download')" />
          </button>
        </div>
      </div>
    </Modal>
  </ClientOnly>
</template>

<script setup>
  defineOptions({
    auth: 'local',     
  })

  const props = defineProps({
    category: {
      type: String,
      required: true
    }
  });

  const observer = ref(null);
  const triggerElement = ref(null);
  const dataFetchInProgress = ref(false);
  const currentCollection = ref(null);
  const resultsCurrentPage = ref(1);
  const downloadingIndex = ref(null);
  const modalImage = ref(null);
  const results = ref({});
  const IMAGES_FORMAT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];

  const { data, filter } = await usePaginatedData({
		endpoint: `media-album/${props.category}/`,
    auth: 'local',
    filters: {
      page: resultsCurrentPage.value,
    }
	});

  const dialogs = reactive({
    image: false
  });
  const { open } = useModal(dialogs);

  function showModal(item) {
    open('image');
    modalImage.value = item;
  }

  async function fetchCategoryData(item) {
    resultsCurrentPage.value = 1;
    currentCollection.value = item.id;
    results.value = {};
    await filter({
      collection: item.id,
    })
    nextTick(() => {
      observeElements();
    });
  }

  async function downloadMedia(item, index) {
    if (index) downloadingIndex.value = index;

    try {
      const response = await useClientFetch(item.meta.url);
      const url = URL.createObjectURL(response)
      const link = document.createElement('a');
      
      link.href = url;
      link.setAttribute('download', item.meta.original_name || 'download');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch(error) {
      console.log('Error downloading media:', error);
    }

    if (index) {
      setTimeout(() => {
        downloadingIndex.value = null;
      }, 200);
    }
  }

  function convertToMB(bytes) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  async function loadNewData() {
    if (dataFetchInProgress.value) return;
    dataFetchInProgress.value = true;
    
    try {
      resultsCurrentPage.value ++;
      await filter({
        page: resultsCurrentPage.value,
        collection: currentCollection.value,
      });
    } catch(error) {
      
    } finally {
      dataFetchInProgress.value = false;
      nextTick(() => {
        observeElements();
      });
    }
  }

  function observeElements() {
    if (observer.value) {
      observer.value.disconnect();
      observer.value = null;
    }

    const elements = document.querySelectorAll('.mediaAlbumList li');
    if (elements.length === 0 || elements.length < 3) return;
    triggerElement.value = elements[elements.length - 3];

    observer.value = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting && data.value?.pagination?.has_next && !dataFetchInProgress.value) {
        loadNewData();
      }
    });

    observer.value.observe(triggerElement.value);
  }

  watch(() => data.value, newValue => {
    let _results = results.value ? JSON.parse(JSON.stringify(results.value)) : {};
    _results = { ...newValue, items: [..._results.items || [], ...newValue?.items || []] };
    results.value = _results;
  }, { immediate: true });

  onMounted(() => {
    nextTick(() => {
      observeElements();
    });
  });

  onBeforeUnmount(() => {
    if (observer.value && triggerElement.value) observer.value.unobserve(triggerElement.value);
  })
</script>

<style lang="scss">
  @import url('@/assets/styles/components/_mediaAlbumList.scss');
</style>